import React, { useCallback, useEffect } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import Select from "react-select"

import Icon from "../../../../../common/icon/icon.component"
import { CustomAttributeDefinitionInputProps, SelectOption } from "../../../../../common/types"
import IntlProps from "../../../../../state/helpers/IntlProps"
import {
    checkEmptyLabelsAndImageUpload, CUSTOM_ATTRIBUTES_MESSAGES, CUSTOM_ATTRIBUTE_EVENT,
    CUSTOM_ATTRIBUTE_TYPES, CUSTOM_ATTRIBUTE_TYPE_SELECT,
    getNumberOfInvalidLabelsAndImageUpload, handleCustomAttributeOperation, handleDrop, mapCustomAttributesFromDto
} from "../../../../../utils"
import "./CustomAttributesDefinitionInput.scss"
import CustomAttributesMultipleSelectWithImageSupport from "./CustomAttributesMultipleSelectWithImageSupport"

const CustomAttributeDefinitionInput = (props: CustomAttributeDefinitionInputProps & IntlProps) => {
    const customAttributes = mapCustomAttributesFromDto(props.customAttributeDefinitions)
    const errorLabelCheck = useCallback(() => checkEmptyLabelsAndImageUpload(customAttributes, props.onError),
        [getNumberOfInvalidLabelsAndImageUpload(customAttributes)]
    )
    useEffect(() => {
        errorLabelCheck()
    }, [
        errorLabelCheck,
    ])

    return <>
        <DragDropContext onDragEnd={results => handleDrop(results, customAttributes, props.onCustomAttributeDefnChange)}>
            <Droppable droppableId="main" type="droppableItem">
                {(provided, snapshot) => (
                    <div
                        id={"#sortable-quote"}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={"group-sortable " + (snapshot.isDraggingOver ? "dnd-highlight" : "")}>
                        {customAttributes.map((input, index) => {
                            if (!input.toDelete) {
                                return <Draggable key={input.id + index} draggableId={input.id + index} index={index}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                        >
                                            <Row className="mb-3">
                                                <Col xs={1}>
                                                    <Row className="d-flex justify-content-center">
                                                        <a
                                                            href={"#"}
                                                            onClick={e => e.preventDefault()}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <Icon className={"lead"} value={"bars"} />
                                                        </a>
                                                    </Row>
                                                </Col>
                                                <Col xs={5}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={
                                                            props.intl.formatMessage(
                                                                CUSTOM_ATTRIBUTES_MESSAGES["custom_attribute_placeholder"]
                                                            )
                                                        }
                                                        onChange={event => handleCustomAttributeOperation(
                                                            customAttributes,
                                                            index,
                                                            event.target.value,
                                                            CUSTOM_ATTRIBUTE_EVENT.UPDATE_ATTRIBUTE_LABEL,
                                                            props.onCustomAttributeDefnChange
                                                        )}
                                                        value={input?.customFieldDefinitionLabel?.label || ""}
                                                    />
                                                </Col>
                                                <Col xs={4}>
                                                    <Select
                                                        searchable={false}
                                                        value={input?.attributeType || ""}
                                                        onChange={(selected: SelectOption<string>) => handleCustomAttributeOperation(
                                                            customAttributes,
                                                            index,
                                                            selected.value,
                                                            CUSTOM_ATTRIBUTE_EVENT.SELECT_ATTRIBUTE_TYPE,
                                                            props.onCustomAttributeDefnChange
                                                        )}
                                                        options={CUSTOM_ATTRIBUTE_TYPES}
                                                        clearable={false}
                                                    />
                                                </Col>
                                                <Col xs={2}>
                                                    <Button
                                                        variant="danger"
                                                        className="mb-2"
                                                        onClick={() => handleCustomAttributeOperation(
                                                            customAttributes,
                                                            index,
                                                            undefined,
                                                            CUSTOM_ATTRIBUTE_EVENT.DELETE_ATTRIBUTE,
                                                            props.onCustomAttributeDefnChange
                                                        )}>
                                                        <FormattedMessage
                                                            id="product.custom_attribute.remove_attr_btn"
                                                            defaultMessage="Remove"
                                                        />
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col xs="1" />
                                                <Col xs="2" >
                                                    <input
                                                        type="checkbox"
                                                        id="custom-attribute-required-check"
                                                        className="mt-2"
                                                        checked={input?.required}
                                                        onChange={() =>
                                                            handleCustomAttributeOperation(
                                                                customAttributes,
                                                                index,
                                                                undefined,
                                                                CUSTOM_ATTRIBUTE_EVENT.CHECK_REQUIRED,
                                                                props.onCustomAttributeDefnChange
                                                            )
                                                        }
                                                    >
                                                    </input>
                                                    <label className="form-check-label">
                                                        {props.intl.formatMessage(
                                                            CUSTOM_ATTRIBUTES_MESSAGES["required_custom_attribute_label"]
                                                        )}
                                                    </label>
                                                </Col>
                                                {input.attributeType === CUSTOM_ATTRIBUTE_TYPE_SELECT.value &&
                                                    (<Col xs="4">
                                                        <input
                                                            type="checkbox"
                                                            id="custom-attribute-allow-more-than-one-choices-check"
                                                            className="mt-2"
                                                            checked={input?.allowMoreThanOneChoice}
                                                            onChange={() =>
                                                                handleCustomAttributeOperation(
                                                                    customAttributes,
                                                                    index,
                                                                    undefined,
                                                                    CUSTOM_ATTRIBUTE_EVENT.CHECK_ALLOW_MORE_THAN_ONE_CHOICE,
                                                                    props.onCustomAttributeDefnChange
                                                                )
                                                            }
                                                        >
                                                        </input>
                                                        <label className="form-check-label">
                                                            {
                                                                props.intl.formatMessage(
                                                                    CUSTOM_ATTRIBUTES_MESSAGES["allow_more_than_more_choice_label"]
                                                                )
                                                            }
                                                        </label>
                                                    </Col>)}
                                            </Row>
                                            <Col xs="4" />
                                            {input.attributeType === CUSTOM_ATTRIBUTE_TYPE_SELECT.value &&
                                                <Row className="mb-3">
                                                    <Col xs="1" />
                                                    <Col xs="10">
                                                        <CustomAttributesMultipleSelectWithImageSupport
                                                            onError={props.onError}
                                                            attributeIndex={index}
                                                            customAttributes={customAttributes}
                                                            onCustomAttributeDefnChange={props.onCustomAttributeDefnChange}
                                                        />
                                                    </Col>
                                                </Row>

                                            }
                                            <hr />
                                        </div>)}
                                </Draggable>
                            }
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>


        <Row className="mb-3">
            <Col xs="auto">
                <ButtonGroup aria-label="Add an attribute" >
                    <Button onClick={() => handleCustomAttributeOperation(
                        customAttributes,
                        undefined,
                        undefined,
                        CUSTOM_ATTRIBUTE_EVENT.ADD_ATTRIBUTE,
                        props.onCustomAttributeDefnChange
                    )} variant="primary">
                        <FormattedMessage
                            id="product.custom_attribute.add_attr_btn"
                            defaultMessage="Add Custom Attribute"
                        />
                    </Button>
                </ButtonGroup>
            </Col>
        </Row>
        {
            props.isError &&
            <Row className="text-danger mb-2 p-2">
                <Col xs="auto">
                    <small>
                        <Icon value={"exclamation"} className={"mr-1"} />
                        <FormattedMessage
                            id="product.custom_attribute.error_message"
                            defaultMessage="Please fill out all custom attribute labels to continue"
                        />
                    </small>
                </Col>
            </Row>
        }
    </>
}

export default injectIntl(CustomAttributeDefinitionInput)


// WEBPACK FOOTER //
// ./src/product/form/components/CustomAttributesDefinition/components/CustomAttributesDefinitionInput.tsx